import type { DxfMappingRole } from '@frontend/api';
import { type Store, createLens } from '@frontend/viewer-core';

import type { PointerTracker } from './pointerTracker';
import type {
  BrushToolId,
  RoleChromeManager,
  MarqueeController,
  MeasureManager,
  SelectionManager,
  ToolId,
  ToolManager,
} from './types';
import { type DxfSessionState } from './types';

export const createToolSlice = (state: Store<DxfSessionState>): Store<ToolId> =>
  createLens(
    state,
    (current) => current.tool,
    (current, tool) => ({ ...current, tool }),
  );

/** The tool a role's brush arms. */
export const brushToolId = (role: DxfMappingRole): BrushToolId =>
  `brush-${role}`;

export const isBrushTool = (tool: ToolId): tool is BrushToolId =>
  tool.startsWith('brush-');

/** Inverse of `brushToolId`. Only meaningful when `isBrushTool` is true. */
export const roleOfBrushTool = (tool: BrushToolId): DxfMappingRole =>
  tool.slice('brush-'.length) as DxfMappingRole;

type Dependencies = {
  tool: Store<ToolId>;
  selection: Pick<SelectionManager, 'clear'>;
  measure: Pick<MeasureManager, 'clear'>;
  marqueeController: Pick<MarqueeController, 'abort'>;
  pointerTracker: Pick<PointerTracker, 'reset'>;
  roleChrome: Pick<RoleChromeManager, 'close'>;
};

export const createToolController = ({
  tool,
  selection,
  measure,
  marqueeController,
  pointerTracker,
  roleChrome,
}: Dependencies): ToolManager => {
  const set = (next: ToolId) => {
    // Bail out entirely on a redundant call: a tool switch is a change of
    // intent, but re-picking the tool already active is not one, and must not
    // cancel a gesture or wipe pointer feedback that had nothing to do with it.
    if (tool.getState() === next) {
      return;
    }

    tool.setState(() => next);
    // Switching tools discards all of these, per the spec: neither a
    // selection nor a half-finished measurement means anything under a new
    // tool, and a context menu keyed to the selection that just vanished has
    // nothing left to act on.
    selection.clear();
    measure.clear();
    marqueeController.abort();
    pointerTracker.reset();
    roleChrome.close();
  };

  return {
    getState: tool.getState,
    subscribe: tool.subscribe,
    set,
    armBrush(role) {
      set(brushToolId(role));
    },
  };
};
